import Service, { type Registry as Services, service } from '@ember/service';
import { capitalize } from '@ember/string';
import Model from '@ember-data/model';
// eslint-disable-next-line import-x/no-named-as-default
import DS from 'ember-data';
import { Changeset } from 'ember-changeset';
import { BufferedChangeset } from 'validated-changeset';
import lookupValidator from 'ember-changeset-validations';
import { type Validation } from 'uplisting-frontend/validations';
import type ModelRegistry from 'ember-data/types/registries/model';
import { type RepositoryCache } from 'uplisting-frontend/utils/interfaces';
import config from 'uplisting-frontend/config/environment';

// NOTE: Those are types to make TS, changeset & ember work together
// so here we are scaling changeset types, to also include the types and properties
// of the model that will be create
type ModelAttributes<T extends Model> = Pick<T, Exclude<keyof T, keyof Model>>;

type ModelProperties<T extends Model> = Readonly<
  Pick<T, Exclude<keyof T, keyof ModelAttributes<T>>>
>;

type ChangesetAttributes<T> = T extends Model
  ? ModelAttributes<T> & ModelProperties<T>
  : T;

export type GenericChangeset<T> = BufferedChangeset &
  ChangesetAttributes<T> & { data: T };

/**
 * Other services which are directly working with models
 * Should inherit from this one
 */
export default abstract class BaseRepositoryService<
  T extends Model,
  K = void,
> extends Service {
  @service store!: Services['store'];
  @service sentry!: Services['sentry'];
  @service notifications!: Services['notifications'];

  abstract recordName: keyof ModelRegistry & string;
  abstract implementMethods: string[];

  validation!: Validation;

  protected cache: RepositoryCache<K> = {};

  public async findAll(options: object = {}): Promise<DS.PromiseArray<T>> {
    this.#assureMethodImplemented('findAll');

    try {
      return this.store.findAll(this.recordName, options);
    } catch (err) {
      this.#handleError(err, 'findAll', { options });

      throw err;
    }
  }

  public peekAll(): T[] {
    this.#assureMethodImplemented('peekAll');

    return this.store.peekAll(this.recordName).slice();
  }

  public peekRecord(id: string): T {
    this.#assureMethodImplemented('peekRecord');

    return this.store.peekRecord(this.recordName, id);
  }

  public async findRecord(
    id: string,
    options?: object,
  ): Promise<DS.PromiseObject<T>> {
    this.#assureMethodImplemented('findRecord');

    try {
      return this.store.findRecord(this.recordName, id, options);
    } catch (err) {
      this.#handleError(err, 'findRecord', { id, options });

      throw err;
    }
  }

  public async save(item: Model): Promise<void> {
    this.#assureMethodImplemented('save');

    try {
      await item.save();
    } catch (err) {
      this.#handleError(err, 'save', {
        recordId: item.id,
        recordType: item.constructor.name,
      });

      throw err;
    }
  }

  public async query(
    query: object,
    options?: object,
  ): Promise<DS.PromiseArray<T>> {
    this.#assureMethodImplemented('query');

    try {
      return this.store.query(this.recordName, query, options);
    } catch (err) {
      this.#handleError(err, 'query', { query, options });

      throw err;
    }
  }

  public async queryRecord(
    query: object,
    options?: object,
  ): Promise<DS.PromiseObject<T>> {
    this.#assureMethodImplemented('queryRecord');

    return this.store.queryRecord(this.recordName, query, options);
  }

  public unloadAll(): void {
    this.#assureMethodImplemented('unloadAll');

    if (this.cache) {
      this.cache = {};
    }

    this.store.unloadAll(this.recordName);
  }

  public unloadRecords(records: Model[]): void {
    this.#assureMethodImplemented('unloadRecords');

    records.forEach((record) => {
      record.unloadRecord();
    });
  }

  public async destroyRecord(item: Model): Promise<void> {
    this.#assureMethodImplemented('destroyRecord');

    try {
      await item.destroyRecord();
    } catch (err) {
      this.#handleError(err, 'destroyRecord', {
        recordId: item.id,
        recordType: item.constructor.name,
      });

      throw err;
    }
  }

  public createRecord(options?: object): T {
    this.#assureMethodImplemented('createRecord');

    return this.store.createRecord(this.recordName, options);
  }

  public buildChangeset(model?: T | object): GenericChangeset<T> {
    const record = model || this.createRecord();

    return Changeset(
      record,
      lookupValidator(this.validation),
      this.validation,
    ) as GenericChangeset<T>;
  }

  protected getDataFromCache(id: string): K | undefined {
    if (this.cache[id]) {
      return this.cache[id];
    }
  }

  /**
   * @description provide better error handling for the repo operations
   *
   * @param error - error object
   * @param operation - operation that failed
   * @param context - additional context about the operation
   */
  #handleError(
    error: unknown,
    operation: string,
    context: Record<string, unknown> = {},
  ): void {
    const errorContext = {
      tags: {
        message: (error as Error)?.message ?? 'Unknown error message',
        operation,
      },
      extra: {
        repository: this.constructor.name,
        operation,
        recordName: this.recordName,
        ...context,
      },
    };

    this.sentry.captureException(error, errorContext);
  }

  /**
   * @description Raise error if operation is not implemented
   * As all methods for models are inherited from this base repositories service
   * And some of them may not have/need methods described in this class
   * According to the Liskov substitution principle they should throw error
   *
   * @param {String} method - name of the operation to test
   */
  #assureMethodImplemented(method: string): void {
    if (!this.implementMethods.includes(method)) {
      const message = `${capitalize(
        this.recordName,
      )}: method '${method}' is not implemented`;

      this.#debug(message);
    }
  }

  #debug(reason: string): void {
    const message = `${this.constructor.name}: ${reason}`;

    if (config.IS_PROD) {
      this.sentry.captureMessage(message);
    } else {
      throw new Error(message);
    }
  }
}
